import { useEffect, useRef, useState } from 'react'
import { Paper, Box, TextField, Button, Chip, Typography } from '@mui/material'
import { getSession, sendMessage, subscribeStream } from '../../api/assistant'

export default function AssistantPage() {
  const [log, setLog] = useState([])
  const [input, setInput] = useState('')
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState(false)
  const [memory, setMemory] = useState(null)
  const logRef = useRef(null)
  const fileInputRef = useRef(null)
  const abortStreamRef = useRef(null)

  useEffect(() => { logRef.current?.scrollTo(0, logRef.current.scrollHeight) }, [log])

  // Close any in-flight SSE stream when the page goes away.
  useEffect(() => () => abortStreamRef.current?.(), [])

  const refreshMemory = async (sessionId) => {
    try {
      const session = await getSession(sessionId)
      setMemory(session?.memory_note ?? null)
    } catch {
      // The chip is advisory; a failed refresh must not disturb the chat.
    }
  }

  const send = async () => {
    const text = input.trim()
    if (!text) return
    // The attachment applies to this message only.
    const attached = file
    setInput('')
    setFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
    setLog((l) => [...l, { kind: 'you', text }])
    setBusy(true)
    try {
      const res = await sendMessage(text, attached)
      if (res.session_id) refreshMemory(res.session_id)
      if (res.needs) {
        setLog((l) => [...l, { kind: 'assistant', text: `I still need: ${res.needs.join(', ')}` }])
        setBusy(false)
        return
      }
      abortStreamRef.current = subscribeStream(res.job_id, (ev) => {
        if (ev.status === 'running') setLog((l) => [...l, { kind: 'status', text: `working… ${ev.progress}%` }])
        if (ev.status === 'completed') {
          setLog((l) => [...l, { kind: 'assistant', text: JSON.stringify(ev.result) }])
          setBusy(false)
        }
        if (ev.status === 'failed') {
          setLog((l) => [...l, { kind: 'error', text: 'failed' }])
          setBusy(false)
        }
      })
    } catch {
      setLog((l) => [...l, { kind: 'error', text: 'send failed' }])
      setBusy(false)
    }
  }

  return (
    <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', height: '70vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2">Caliper assistant</Typography>
        <Chip size="small" color={busy ? 'info' : 'default'} label={busy ? 'working' : 'idle'} />
        {memory && <Chip size="small" variant="outlined" label={`memory: ${memory}`} />}
        {file && <Chip size="small" variant="outlined" label={file.name} />}
      </Box>
      <Box ref={logRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 1.5 }}>
        {log.map((e, i) => (
          <Typography key={i} variant="body2" color={e.kind === 'error' ? 'error' : e.kind === 'you' ? 'primary' : 'text.primary'}>
            {e.text}
          </Typography>
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, p: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button variant="outlined" size="small" onClick={() => fileInputRef.current?.click()}>
          Attach doc
        </Button>
        <input ref={fileInputRef} data-testid="doc-file-input" type="file" hidden
          accept=".docx,.pdf,.txt"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        <TextField size="small" fullWidth value={input} placeholder="Tell Caliper what to do…"
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) send() }} />
        <Button variant="contained" size="small" onClick={send}>Send</Button>
      </Box>
    </Paper>
  )
}
